import { useEffect, useRef, useCallback } from 'react';
import { API_CONFIG } from '../config';
import { apiFetch } from '../utils/api';
import { isSuperAdmin } from '../types';
import { useTranslation } from './useTranslation';
import type { AddNotificationOptions } from './useNotifications';
import type { NotificationType } from '../components/ui/Notification';

interface PendingAccessRequest {
  id: string;
  email: string;
  applicantName: string;
  claimedProfileType: string;
  claimedRole: string;
  status: string;
}

interface UsePendingAccessRequestsArgs {
  userRole: string | null | undefined;
  addNotification: (
    type: NotificationType,
    title: string,
    message?: string,
    durationOrOptions?: number | AddNotificationOptions
  ) => string;
  removeByPersistentId: (persistentId: string) => void;
  showSuccess: (title: string, message?: string) => void;
  showError: (title: string, message?: string) => void;
}

export function usePendingAccessRequests({
  userRole,
  addNotification,
  removeByPersistentId,
  showSuccess,
  showError,
}: UsePendingAccessRequestsArgs): void {
  const { t } = useTranslation();
  const loadedRef = useRef<Set<string>>(new Set());
  const handlersRef = useRef({ addNotification, removeByPersistentId, showSuccess, showError, t });
  handlersRef.current = { addNotification, removeByPersistentId, showSuccess, showError, t };

  const resolveRequest = useCallback(async (id: string, action: 'approve' | 'reject') => {
    const { removeByPersistentId: remove, showSuccess: ok, showError: fail, t: tr } =
      handlersRef.current;
    try {
      const response = await apiFetch(`${API_CONFIG.API_URL}/access-requests/${id}/${action}`, {
        method: 'POST',
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || `Failed to ${action}`);
      remove(id);
      loadedRef.current.delete(id);
      if (action === 'approve') {
        ok(
          tr('accessRequest.approvedTitle'),
          data.emailSent
            ? tr('accessRequest.approvedWithEmail')
            : tr('accessRequest.approvedNoEmail')
        );
      } else {
        ok(tr('accessRequest.rejectedTitle'), tr('accessRequest.rejectedMessage'));
      }
    } catch (err) {
      fail(
        tr('accessRequest.actionFailed'),
        err instanceof Error ? err.message : 'Error'
      );
    }
  }, []);

  useEffect(() => {
    if (!isSuperAdmin(userRole || null)) {
      for (const id of Array.from(loadedRef.current)) {
        removeByPersistentId(id);
      }
      loadedRef.current.clear();
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const response = await apiFetch(`${API_CONFIG.API_URL}/access-requests/pending`);
        if (!response.ok) {
          console.error(
            `Failed to load pending access requests: HTTP ${response.status}`,
            await response.text().catch(() => '')
          );
          return;
        }
        const data = await response.json();
        const requests: PendingAccessRequest[] = data.requests || [];
        if (cancelled) return;

        const { addNotification: add, t: tr } = handlersRef.current;
        const seen = new Set(requests.map((r) => r.id));

        for (const id of Array.from(loadedRef.current)) {
          if (!seen.has(id)) {
            handlersRef.current.removeByPersistentId(id);
            loadedRef.current.delete(id);
          }
        }

        for (const req of requests) {
          if (loadedRef.current.has(req.id)) continue;
          loadedRef.current.add(req.id);
          add(
            'warning',
            tr('accessRequest.toastTitle'),
            tr('accessRequest.toastMessage', {
              name: req.applicantName,
              email: req.email,
              role: req.claimedRole || req.claimedProfileType,
            }),
            {
              duration: 0,
              persistentId: req.id,
              actions: [
                {
                  label: tr('accessRequest.approve'),
                  variant: 'primary',
                  onClick: () => resolveRequest(req.id, 'approve'),
                },
                {
                  label: tr('accessRequest.reject'),
                  variant: 'danger',
                  onClick: () => resolveRequest(req.id, 'reject'),
                },
              ],
            }
          );
        }
      } catch (err) {
        console.error('Failed to load pending access requests:', err);
      }
    };

    void load();
    const interval = setInterval(() => void load(), 60000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [userRole, resolveRequest, removeByPersistentId]);
}
